import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ratesService } from "../../../services/ratesService";
import { AdminLayout } from "../../layout/AdminLayout";
import { DataTable } from "../../../components/DataTable";
import { toast } from "react-toastify";
import { Edit, Trash2, Plus } from "lucide-react";

export const RatesList = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    loadRates();
  }, [page, pageSize, search, sortBy, sortOrder]);

  const loadRates = async () => {
    try {
      setLoading(true);
      const data = await ratesService.getAll(
        page,
        pageSize,
        search,
        sortBy,
        sortOrder,
      );
      setRates(data.data || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.log("Error loading rates:", error);
      toast.error("Failed to load rates");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure?")) return;

    try {
      await ratesService.delete(id);
      toast.success("Rate deleted");
      loadRates();
    } catch (error) {
      console.log("Error deleting rate:", error);
      toast.error("Failed to delete rate");
    }
  };

  const columns = [
    {
      key: "role_name",
      label: "Role",
      sortable: true,
      width: "30%",
      render: (row) => row.role_id?.name || "-",
    },
    {
      key: "category_name",
      label: "Category",
      sortable: true,
      width: "30%",
      render: (row) => row.category_id?.name || "-",
    },
    {
      key: "competency",
      label: "Competency",
      sortable: true,
      width: "15%",
    },
    {
      key: "rate",
      label: "Rate ($)",
      sortable: true,
      width: "15%",
      render: (row) =>
        row.rate !== undefined && row.rate !== null
          ? Number(row.rate).toFixed(2)
          : "-",
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      width: "20%",
      render: (row) => (
        <div className="flex gap-2">
          <Link
            to={`/admin/rates/${row._id}`}
            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <Edit size={16} />
            Edit
          </Link>
          <button
            onClick={() => handleDelete(row._id)}
            className="text-red-600 hover:text-red-800 flex items-center gap-1"
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Rates</h1>
          <Link
            to="/admin/rates/new"
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
          >
            <Plus size={20} />
            Add Rate
          </Link>
        </div>

        <DataTable
          columns={columns}
          data={rates}
          loading={loading}
          totalItems={total}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSearch={setSearch}
          onSort={(column, order) => {
            setSortBy(column);
            setSortOrder(order);
          }}
          searchPlaceholder="Search by role, category, competency, or rate..."
          sortBy={sortBy}
          sortOrder={sortOrder}
        />
      </div>
    </AdminLayout>
  );
};
